import register from 'preact-custom-element';
import { Component } from 'preact'
import { useContext } from "preact/hooks";

import ApiLayer from '../api/api'
import { receivedData } from 'renette-api/dist/types';
import { Table } from '../components/Table';
import { fields } from './home.types';

import * as Sentry from '@sentry/react';
import '../plugins/polyfills.js'
import '../styles/home.scss'
import { Legend } from '../components/Legend';
import classNames from 'classnames';
import { AppStateContext } from '../plugins/state';
import { SubfolderRouter } from '../components/SubFolderRouter';
import { ApplicationState, Meal, application } from '../api/api.types';
import _ from 'lodash'

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0.5,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: import.meta.env.PROD ? 0.8 : 1.0,
  });
}

type Props = {};
type State = {
  errorMessage: string | null,
  truncateCells: boolean,
};

type MealCount = { all: number, vege: number, free: number };
type MealCounts = { [meal in Meal]: MealCount };

export class Home extends Component<Props, State> {
  private subscribed = false;
  constructor(props: Props) {
    super(props);

    this.state = {
      errorMessage: null,
      truncateCells: true,
    };
  }
  makeErrorMessage(response: receivedData<any>): string {
    Sentry.captureEvent({
      extra: response
    });
    return `Error (code ${response.code}) while performing ${response.action}: ${JSON.stringify(response.data)}`;
  }

  render(props: Props, state: Readonly<State>) {
    const globalState = useContext(AppStateContext);
    if (!this.subscribed) {
      this.subscribed = true;
      globalState.onChange = () => this.setState({});
    }
    const days = getStatistics(globalState.applications);
    const order = { [Meal.SNIDANE]: 0, [Meal.OBED]: 1, [Meal.VECERE]: 2 }
    const _home = this;

    const filteredApplications = (globalState.showCancelled ? globalState.applications : globalState.applications?.filter(a => a.state !== ApplicationState.cancelled))?.map(a=>({
      ...a,
      accommodation: globalState.config?.accommodation.find(t=>t.key == a.accommodation)?.text ?? a.accommodation,
      group: globalState.config?.groups.find(g=>g.key == a.group)?.text ?? a.group
    }))

    async function resendEmail(selected: application | application[] | null, _: any, to?: string) {
      try {
        if (Array.isArray(selected)) {
          // Multiple lines were selected
          for (let row of selected) {
            const response = await ApiLayer.resendEmail(row.appID, to);
            if (response.code != 200) {
              alert(_home.makeErrorMessage(response));
            }
          }
        }
        else if (selected) {
          const response = await ApiLayer.resendEmail(selected.appID, to);
          if (response.code != 200) {
            alert(_home.makeErrorMessage(response));
          }
        }
        else {
          console.log("No line was selected");
        }
      } catch (e) {
        console.error(e)
        Sentry.captureException(e);
        alert(e);
      }
    }

    return <>
      <Legend
        events={globalState.events}
        onSelectEvent={e => {
          globalState.selectedEventID = parseInt(e.currentTarget.value);
          globalState.onChange();
        }}
        onChangeIncludePast={e => {
          globalState.includePastEvents = e.currentTarget.checked;
          globalState.init();
        }}
        onGenerateCSV={async () => {
          if (filteredApplications) {
            const exportToCsv = await import('export-to-csv')
            const csvConfig = exportToCsv.mkConfig({
              filename: `applications-${new Date().toLocaleString(navigator.language)}`,
              columnHeaders: fields.map(f => f.propName),
            })

            const csvData = filteredApplications.map(a => _.mapValues(a, v => v?.toString()))

            exportToCsv.download(csvConfig)(exportToCsv.generateCsv(csvConfig)(csvData))
          }
        }}
        selectedEventID={globalState.selectedEventID}
        loginHandler={SubfolderRouter.handler}
        includePast={globalState.includePastEvents}
      />
      <div className="legend">
        <strong>Statistics</strong>
        <label className="info">{"\u{1F9D1}\u{200D}\u{1F91D}\u{200D}\u{1F9D1}"} <span>{filteredApplications?.length}</span></label>
        {
          // Generate statistics for each day
          days.map(([date, { places, meals }]) => <label className="info">{new Date(date).getDate()}.{new Date(date).getMonth() + 1}. {places} {"\u{1F9D1}\u{200D}\u{1F4BC}"} &nbsp;
            {// Sort meals by order
              Object.entries(meals).sort((a, b) => {
                return order[a[0] as any as keyof typeof order] - order[b[0] as any as keyof typeof order];
              }).map(([meal, count], i, a) => count.all > 0 ?
                <span key={meal}>{meal[0]}: {count.all}<small>{specialMealToStr(count)}</small>{i == a.length - 1 ? '' : ', '}</span>
                : null)}
          </label>)
        }
        <label className="info">Person×nights: <span>{filteredApplications?.map(a => Math.floor((a.departure.getTime() - a.arrival.getTime()) / (1000 * 3600 * 24))).reduceRight((a, b) => a + b, 0)}</span></label>
        <label className="info">Cancelled: <span>{globalState.applications?.filter(a => a.state == ApplicationState.cancelled).length}</span></label>
        <label className="info">Emails sent: <span>{filteredApplications?.filter(a => a.confirmation_sent).length}</span>
          {
            !filteredApplications?.every(a => a.confirmation_sent) &&
            <>&ensp;<button title="Re-send emails where not sent" onClick={() => {
              globalState.resendMailIfNotSent();
            }}>💌</button></>
          }</label>
        <label className="info">Wrap text: <input type="checkbox" checked={!state.truncateCells} onChange={() => this.setState({ truncateCells: !state.truncateCells })} /></label>
        <label className="info">Include cancelled: <input type="checkbox" checked={globalState.showCancelled} onChange={() => {
          globalState.showCancelled = !globalState.showCancelled
          globalState.onChange()
        }} /></label>
      </div>
      {(filteredApplications ?? null) !== null &&
        <Table
          className={
            classNames({
              truncate: state.truncateCells
            })
          }
          data={filteredApplications!}
          fields={fields}
          showIndexColumn={true}
          checkboxes={true}
          filters={true}
          defaultActions={[
            {
              text: "Re-send confirmation email",
              onClick: resendEmail
            },
            {
              text: "Send to different address",
              onClick: (selected) => {
                const to = prompt("Enter email address", Array.isArray(selected) ? selected[0]?.email : selected?.email);
                if (to) resendEmail(selected, null, to);
              }
            }
          ]} />}
    </>
  }
}

const empty: MealCount = {
  all: 0,
  vege: 0,
  free: 0
}
function mealType(a: application): MealCount {
  return {
    all: 1,
    vege: a.note_food.includes('vege') ? 1 : 0,
    free: a.note_food.includes('bezlep') ? 1 : 0
  }
}
function specialMealToStr(count: MealCount): string {
  return count.vege || count.free ? `(${count.vege}v, ${count.free}b)` : '';
}
function addMeals(a: MealCounts, b: MealCounts): MealCounts {
  return {
    [Meal.OBED]: {
      all: a[Meal.OBED].all + b[Meal.OBED].all,
      vege: a[Meal.OBED].vege + b[Meal.OBED].vege,
      free: a[Meal.OBED].free + b[Meal.OBED].free
    },
    [Meal.SNIDANE]: {
      all: a[Meal.SNIDANE].all + b[Meal.SNIDANE].all,
      vege: a[Meal.SNIDANE].vege + b[Meal.SNIDANE].vege,
      free: a[Meal.SNIDANE].free + b[Meal.SNIDANE].free
    },
    [Meal.VECERE]: {
      all: a[Meal.VECERE].all + b[Meal.VECERE].all,
      vege: a[Meal.VECERE].vege + b[Meal.VECERE].vege,
      free: a[Meal.VECERE].free + b[Meal.VECERE].free
    }
  }

}
function getStatistics(applications: application[] | null) {
  if (!applications) return [];

  const days = new Map<number, {
    places: number,
    meals: MealCounts
  }>();
  for (const a of applications) {
    // JS will automatically uodate month and year
    const meal = mealType(a);
    for (let day = new Date(a.arrival), meals = {
      [Meal.OBED]: [Meal.OBED, Meal.SNIDANE].includes(a.first_meal) ? meal : empty,
      [Meal.SNIDANE]: a.first_meal === Meal.SNIDANE ? meal : empty,
      [Meal.VECERE]: meal,
    }; day <= a.departure; day.setDate(day.getDate() + 1), meals = day.getTime() == a.departure.getTime()/* JS compares Date by ref */ ? {
      [Meal.OBED]: [Meal.OBED, Meal.VECERE].includes(a.last_meal) ? meal : empty,
      [Meal.SNIDANE]: meal,
      [Meal.VECERE]: a.last_meal === Meal.VECERE ? meal : empty,
    } : {
      [Meal.OBED]: meal,
      [Meal.SNIDANE]: meal,
      [Meal.VECERE]: meal,
    }) {
      const current = days.get(day.getTime());
      if (typeof current === 'undefined') {
        days.set(day.getTime(), { places: 1, meals });
      } else {
        days.set(day.getTime(), {
          places: current.places + 1,
          meals: addMeals(current.meals, meals)
        });
      }
    }
  }
  //return as array sorted by date
  return Array.from(days.entries()).sort((a, b) => a[0] - b[0]);
}

const name = 'x-matesova-prace'
window?.customElements.get(name) || register(Home, name, ['eventID'], { shadow: true });